import { buildApiErrorMessage, toApiError, type PaginatedResponse } from "@/shared/api/common";
import { http } from "@/shared/api/http";
import type { OrganizationRegion, OrganizationType } from "@/shared/config/constants";

export type Organization = {
  id: string;
  slug?: string | null;
  name: string;
  phones: string[];
  address?: string | null;
  city?: string | null;
  region?: OrganizationRegion | null;
  district?: string | null;
  type?: OrganizationType | null;
  logo_sm?: string | null;
  logo_big?: string | null;
  approved_date?: string | null;
  lastPriceFileDate?: string | null;
  wholesale_offer_count?: number;
  highlights?: Partial<Record<string, string>> | null;
};

export type CreateOrganizationPayload = {
  name: string;
  phones?: string[];
  address?: string | null;
  city?: string | null;
  region?: OrganizationRegion | null;
  district?: string | null;
  type?: OrganizationType | null;
  logo_sm?: string | null;
  logo_big?: string | null;
  logo_base64?: string | null;
};

export async function fetchOrganizations(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    type?: OrganizationType;
    approved?: boolean;
    engine?: "typesense" | "postgres";
    sort_by?: "name" | "wholesale_offer_count";
    sort_order?: "asc" | "desc";
    wholesale_offer_region?: string | null;
  } = {},
) {
  try {
    const response = await http.get<PaginatedResponse<Organization>>("/organizations", {
      params: {
        page: params.page,
        limit: params.limit,
        q: params.search?.trim() || undefined,
        type: params.type,
        approved: params.approved,
        engine: params.engine,
        sort_by: params.sort_by,
        sort_order: params.sort_order,
        wholesale_offer_region: params.wholesale_offer_region || undefined,
      },
    });

    return response.data;
  } catch (error) {
    throw toApiError(error, "Unable to load organizations.");
  }
}

export async function fetchWholesaleOfferOrganizationFilterOptions(
  params: {
    search?: string;
    wholesale_offer_region?: string | null;
  } = {},
): Promise<{ data: Organization[]; engine?: string }> {
  try {
    const pageSize = 100;
    const firstPage = await http.get<PaginatedResponse<Organization>>("/organizations", {
      params: {
        page: 1,
        limit: pageSize,
        q: params.search?.trim() || undefined,
        approved: true,
        type: "wholesale",
        engine: "postgres",
        sort_by: "wholesale_offer_count",
        sort_order: "desc",
        wholesale_offer_region: params.wholesale_offer_region || undefined,
      },
    });
    const organizations = [...firstPage.data.data];
    const totalPages = firstPage.data.pagination?.totalPages ?? 1;

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await http.get<PaginatedResponse<Organization>>("/organizations", {
        params: {
          page,
          limit: pageSize,
          q: params.search?.trim() || undefined,
          approved: true,
          type: "wholesale",
          engine: "postgres",
          sort_by: "wholesale_offer_count",
          sort_order: "desc",
          wholesale_offer_region: params.wholesale_offer_region || undefined,
        },
      });
      organizations.push(...response.data.data);
    }

    return {
      data: organizations.filter((org) => (org.wholesale_offer_count ?? 0) > 0),
      engine: (firstPage.data as PaginatedResponse<Organization> & { engine?: string }).engine,
    };
  } catch (error) {
    throw toApiError(error, "Unable to load organization filter options.");
  }
}

export async function createOrganization(payload: CreateOrganizationPayload) {
  try {
    const response = await http.post<{ data: Organization }>("/organizations", payload);
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to create organization.");
  }
}

export type UpdateOrganizationPayload = {
  name?: string;
  phones?: string[];
  address?: string | null;
  city?: string | null;
  region?: OrganizationRegion | null;
  district?: string | null;
  type?: OrganizationType | null;
  logo_sm?: string | null;
  logo_big?: string | null;
  logo_base64?: string | null;
};

export async function fetchMyOrganization() {
  try {
    const response = await http.get<{ data: Organization }>("/organizations/me");
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to load organization.");
  }
}

export async function fetchOrganizationById(id: string) {
  try {
    const response = await http.get<{ data: Organization }>(`/organizations/${id}`);
    return response.data.data;
  } catch (error) {
    throw new Error(buildApiErrorMessage(error, "Unable to load organization."));
  }
}

export async function updateOrganization(id: string, payload: UpdateOrganizationPayload) {
  try {
    const response = await http.patch<{ data: Organization }>(`/organizations/${id}`, payload);
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to update organization.");
  }
}

export async function fetchOrganizationAutocomplete(query: string): Promise<string | null> {
  const response = await http.get<{ suggestion: string | null }>("/app/organizations/autocomplete", {
    params: { q: query },
  });
  return response.data.suggestion;
}

export type OrganizationRankingSortBy =
  "overall" | "cheapest_pct" | "variety" | "promotion_pct" | "freshness" | "medicines";

export type OrganizationRanking = {
  organization_id: string;
  name: string;
  slug: string | null;
  city: string | null;
  region: OrganizationRegion | null;
  logo_sm: string | null;
  type: OrganizationType;
  metrics: {
    total_offers: number;
    distinct_medicines: number;
    exclusive_medicines: number;
    active_days: number;
    promotion_count: number;
    cheapest_count: number;
    expensive_count: number;
    latest_update: string | null;
  };
  ratios: {
    cheapest_pct: number;
    promotion_pct: number;
    variety_norm: number;
    freshness_norm: number;
    medicines_norm: number;
  };
  score: {
    overall: number;
    price: number;
    variety: number;
    freshness: number;
    promotion: number;
    medicines: number;
  };
};

export async function fetchOrganizationRankings(params: {
  region?: OrganizationRegion;
  payment_percentage?: number;
  sort_by?: OrganizationRankingSortBy;
  window_days?: number;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<OrganizationRanking>> {
  try {
    const response = await http.get<PaginatedResponse<OrganizationRanking>>("/v1/public/organization-rankings", {
      params: {
        region: params.region,
        payment_percentage: params.payment_percentage,
        sort_by: params.sort_by,
        window_days: params.window_days,
        page: params.page,
        limit: params.limit,
      },
    });
    return response.data;
  } catch (error) {
    throw toApiError(error, "Unable to load organization rankings.");
  }
}

export type { PaginationMeta } from "@/shared/api/common";
